// client/src/pages/sections/groups/resolveVerbConfig.js
// UI metadata for each Resolve verb. Mirrors the server-side registry at
// api/shared/resolutionVerbs.js — verb keys MUST match.
//
// `mark_nlc` is NOT listed here because it's surfaced as a per-MemberCard
// button (MarkNlcButton.js), not as a Resolve-modal verb. The Resolve modal
// is only for issue-level verbs that close a specific dq_issue.

// Verb "kinds" drive button color + group order in the modal.
//   confirm — "this is the right answer" closes (no data change)
//   move    — re-parent / re-route (writes sg_parent_code / rg_parent_sg_code)
//   exclude — remove from analysis (dismiss; mark_nlc is per-card not here)
//   defer   — snooze until next ingest
export const VERB_KINDS = ['confirm', 'move', 'exclude', 'defer'];

// Quick-pick source for the RG anchor pickers: the caller's OWN Relationship
// Groups (the ~95% case). Returns [{ clientCode, clientName }] for the
// AnchorPickerField to render as one-click options above the full search.
async function loadMyRgAnchors() {
  try {
    const r = await fetch('/api/dgMyRgAnchors', { credentials: 'include' });
    if (!r.ok) return [];
    const data = await r.json();
    return data.anchors || [];
  } catch {
    return [];   // fall back to search-only
  }
}

// Mirror of api/shared/smllcTypes.js — keep in sync. Only these PE ownership types can be a single-member
// LLC on a personal return (ruled 2026-10-27); every other business is a confirmed non-SMLLC ('OBZ').
export const SMLLC_ELIGIBLE_TYPES = ['LLC', 'LLP', 'SOLE PROPRIETOR', 'PARTNERSHIP'];

// Mirror of api/shared/resolutionVerbs.js#kindOf — keep in sync.
// Classifies an entity_type string into the SG kind alphabet:
// IND | TE | BIZ (SMLLC-eligible business) | OBZ (any other business) | UNK.
export function kindOf(entityType) {
  if (!entityType) return 'UNK';
  const t = String(entityType).trim().toUpperCase();
  if (t === 'INDIVIDUAL')                           return 'IND';
  if (t === 'UNKNOWN')                              return 'UNK';
  if (['TRUST', 'ESTATE', 'FIDUCIARY'].includes(t)) return 'TE';
  return SMLLC_ELIGIBLE_TYPES.includes(t) ? 'BIZ' : 'OBZ';
}

// Mirror of api/shared/resolutionVerbs.js#requiredMixedConfirmation —
// keep in sync. Returns 'smllc', 'dependent', or null based on the
// composition of a would-be Signer Group cohort.
export function requiredMixedConfirmation(memberKinds) {
  if (!Array.isArray(memberKinds) || memberKinds.length < 2) return null;
  const bizN      = memberKinds.filter(k => k === 'BIZ').length;
  const indN      = memberKinds.filter(k => k === 'IND').length;
  const teN       = memberKinds.filter(k => k === 'TE').length;
  const personalN = indN + teN;
  if (bizN >= 1 && personalN >= 1) return 'smllc';   // biz + any personal (IND or Estate/Trust)
  if (teN >= 1 && indN >= 1)       return 'smllc';   // D3: an Estate/Trust joining an Individual — same confirmation
  if (indN >= 2)                   return 'dependent';
  return null;
}

// Mirror of api/shared/resolutionVerbs.js#requiredMixedConfirmations — keep in sync. ONE confirmation
// per move (D1, ruled 2026-10-26): a Business joining a group that already holds Individuals asks for
// 'smllc' only; the Individuals already there are assumed to be dependents. [] = none.
export function requiredMixedConfirmations(memberKinds) {
  const one = requiredMixedConfirmation(memberKinds);
  return one ? [one] : [];
}

// Mirror of api/shared/resolutionVerbs.js#requiredConfirmationsForJoin — keep in sync. What a client (or
// clients) JOINING a group must confirm, judged on who is joining against everyone else who will be there
// (an Individual may have dependents AND SMLLC / Estate / Trust clients; those already there are assumed
// confirmed): a Business or Estate/Trust joining an Individual (or each other) asks 'smllc'; an Individual joining
// an Individual asks 'dependent'; an Individual joining only Business / Estate-Trust asks 'smllc'.
export function requiredConfirmationsForJoin(existingKinds, moverKinds) {
  const out = new Set();
  const movers = moverKinds || [];
  movers.forEach((m, i) => {
    const others = [...(existingKinds || []), ...movers.filter((_, j) => j !== i)];
    const hasInd = others.includes('IND');
    const hasBiz = others.includes('BIZ');
    const hasTe  = others.includes('TE');
    if (m === 'BIZ' && (hasInd || hasTe)) out.add('smllc');
    else if (m === 'TE' && (hasInd || hasBiz)) out.add('smllc');
    else if (m === 'IND') {
      if (hasInd) out.add('dependent');
      else if (hasBiz || hasTe) out.add('smllc');
    }
  });
  return ['smllc', 'dependent'].filter(t => out.has(t));
}

// Friendly checkbox copy for each prediction. Shown in the
// mixed-confirmation row of accept_sg_suggestion / pick_truth.
export const MIXED_CONFIRM_LABEL = {
  smllc:
    'Legitimate only if the Business (an LLC, LLP, Sole Proprietor or Partnership) is a single-member LLC (SMLLC) on the ' +
    'personal (Individual / Estate / Trust) return, or the Estate / Trust files ' +
    "with the Individual in this group — I confirm this applies.",
  dependent:
    'I confirm the second Individual in this group is a dependent minor ' +
    "on the other Individual's return. This will create a two-Individual " +
    'Signer Group.',
};

// accept_sg_suggestion: issue.id -> Map(existing anchor outside the proposal -> entity kinds already in its group).
// Filled by the parent picker's loadOptions so the confirmation toggles (showWhen) know what the chosen anchor brings.
const acceptParentContext = new Map();

// Helper for the SG_SOURCE_DISAGREEMENT case: given the partner's
// current `side` radio selection, returns the required confirmations
// (['smllc'], ['dependent'] or []) based on the chosen side's
// cohort composition.
function pickTruthRequiredConfirms(values, issue) {
  const side = values.side;
  if (!side) return [];
  const details = issue.details || {};
  const members = side === 'knuula' ? details.knuula_members : details.pe_members;
  if (!Array.isArray(members)) return [];
  return requiredMixedConfirmations(members.map(m => kindOf(m.entityType)));
}


// ── rehome_nlc_group (NLC_PARENT_NEEDS_SUCCESSOR) ───────────────────────────
// A client marked No Longer Client was the Signer Group parent (details.layer 'sg') or
// Relationship Group anchor ('rg') of the clients in this issue. The partner chooses who leads
// the group now — same vocabulary as the Mark NLC dialog (MarkNlcButton): promote a remaining
// member / disband / move (SG) or merge (RG). The two anchor pickers are module-level constants
// on purpose: AnchorPickerField's effect is keyed on the field object, so a field rebuilt on
// every render would loop.
const rehomeLabel = (c) => (c.clientName ? `${c.clientCode} — ${c.clientName}` : c.clientCode);

// Codes the move / merge target must not be: the NLC client and the group's own members (to
// lead the group with one of them, choose "promote" instead).
function rehomeExcluded(issue) {
  const d = (issue && issue.details) || {};
  return [d.nlcClientCode, ...((d.members || []).map(m => m.clientCode))].filter(Boolean);
}

const REHOME_SG_MOVE_FIELD = {
  type:        'anchorPicker',
  anchorType:  'sg',
  name:        'targetParent',
  label:       'Pick the existing Signer Group',
  placeholder: 'Start typing a Signer Group code or name…',
  required:    true,
  showWhen:    (values) => values.resolution === 'move',
  excludeAnchor: rehomeExcluded,
};

const REHOME_RG_MERGE_FIELD = {
  type:        'anchorPicker',
  anchorType:  'rg',
  name:        'targetParent',
  label:       'Pick the existing Relationship Group',
  placeholder: 'Start typing a Relationship Group code or name…',
  required:    true,
  showWhen:    (values) => values.resolution === 'merge',
  loadPreferred: loadMyRgAnchors,
  excludeAnchor: rehomeExcluded,
};

// Why the verb can't be applied yet, or null. An RG task waits for the signer group task of the
// same NLC client: a member whose signer group still points at the NLC client has no signer
// group to anchor on. (The server enforces the same rule.)
function rehomeBlockedReason(issue) {
  const d = (issue && issue.details) || {};
  if (d.layer === 'rg' && d.sgElectionPending) {
    return `Some of these clients are still in the Signer Group that ${d.nlcClientCode} used to lead. ` +
           'Choose the new Signer Group parent first (the Signer Group task for the same client ' +
           'above), then come back to pick the Relationship Group anchor.';
  }
  return null;
}

export const VERB_CONFIG = {

  // ── Confirm SMLLC ──────────────────────────────────────────────────
  confirm_smllc: {
    label: 'Confirm SMLLC',
    kind:  'confirm',
    applicableTo: ['SG_BUSINESS_INDIVIDUAL_MIXED'],
    formFields: [],
    confirmation: {
      title: 'Confirm single-member LLC?',
      body:  () =>
        'The Business in this signer group is a single-member LLC on the ' +
        'personal (Individual / Estate / Trust) return — the grouping is ' +
        'correct as-is. The issue will close with no data change.',
      confirmLabel: 'Yes, confirm SMLLC',
      cancelLabel:  'Back',
    },
  },

  // ── Confirm dependent ─────────────────────────────────────────────
  confirm_dependent: {
    label: 'Confirm dependent',
    kind:  'confirm',
    applicableTo: ['SG_MULTI_INDIVIDUAL_PARENT'],
    formFields: [],
    confirmation: {
      title: 'Confirm dependent minor?',
      body:  () =>
        'The Individual child in this signer group is a dependent minor on ' +
        "the parent's return — the grouping is correct as-is. The issue " +
        'will close with no data change.',
      confirmLabel: 'Yes, confirm dependent',
      cancelLabel:  'Back',
    },
  },

  // ── Confirm SG (write Knuula-suggested parent) ─────────────────────
  confirm_sg: {
    label: 'Confirm signer group',
    kind:  'confirm',
    applicableTo: ['SG_NEEDS_CONFIRMATION'],
    formFields: [],
    confirmation: {
      title: 'Confirm the suggested signer group?',
      body:  (issue) => {
        const d = issue.details || {};
        const target = d.suggested_sg || d.knuula_sg || d.pe_sg || '?';
        return `Set ${issue.primaryClientCode} → Signer Group ${target}. ` +
               'Records your confirmed Signer Group for this client.';
      },
      confirmLabel: 'Yes, confirm',
      cancelLabel:  'Back',
    },
  },

  // ── Pick truth (Knuula vs PE) ──────────────────────────────────────
  pick_truth: {
    label: 'Pick the correct signer group',
    kind:  'move',
    applicableTo: ['SG_SOURCE_DISAGREEMENT'],
    formFields: [
      {
        type:    'radio',
        name:    'side',
        label:   'Which source is correct?',
        options: (issue) => {
          const d = issue.details || {};
          return [
            { value: 'knuula', label: `Knuula — ${d.knuula_sg || '(none)'}` },
            { value: 'pe',     label: d.source === 'stored'
                ? `Keep the stored group — ${d.pe_sg || '(none)'}`
                : `PE — ${d.pe_sg || '(none)'}` },
          ];
        },
        required: true,
      },
      // Mixed-confirmation gate: if the partner picks a side whose cohort mixes a Business with a
      // personal return (smllc) or holds two or more Individuals (dependent), the server rejects the
      // call unless they also commit to that confirmation here in the same submission. One checkbox
      // per possible confirmation, shown when the picked side needs it; the dependent one binds to
      // its own name (`mixedConfirmationDependent`), which the server reads alongside `mixedConfirmation`.
      {
        type:         'checkbox',
        name:         'mixedConfirmation',
        label:        MIXED_CONFIRM_LABEL.smllc,
        checkedValue: 'smllc',
        required:     true,
        showWhen:     (values, issue) => pickTruthRequiredConfirms(values, issue).includes('smllc'),
      },
      {
        type:         'checkbox',
        name:         'mixedConfirmationDependent',
        label:        MIXED_CONFIRM_LABEL.dependent,
        checkedValue: 'dependent',
        required:     true,
        showWhen:     (values, issue) => pickTruthRequiredConfirms(values, issue).includes('dependent'),
      },
    ],
    confirmation: {
      title: 'Pick the correct signer group',
      body:  (issue) =>
        `Choose which source reflects reality for ${issue.primaryClientCode}. ` +
        'The chosen side will be written as the Signer Group; the other source ' +
        'will be flagged for cleanup in a later cycle. ' +
        'After you pick, the group is recalculated and any other issues are ' +
        're-evaluated against the corrected Signer Group. ' +
        'If the group itself still needs adjusting (split out a member, change ' +
        'the parent, mark NLC), you can do that from the per-member actions ' +
        'once the new mixed-type warnings — if any — surface below.',
      confirmLabel: 'Apply',
      cancelLabel:  'Back',
    },
  },

  // ── Ignore proposed Signer Group ───────────────────────────────────
  // Partner confirms the clients are correctly NOT in the same SG.
  // Closes the dq_issue with no data change.
  //
  // `hideFromVerbList: true` keeps this verb out of the Resolve
  // modal's verb-selection step — it's surfaced as a dedicated
  // per-row Action column button instead. The verb is still
  // dispatchable via initialVerb (the per-row button uses that
  // path to land the partner directly on the confirmation step).
  ignore_sg_suggestion: {
    label: 'Ignore proposed group',
    kind:  'exclude',
    applicableTo: [
      'SG_SUGGESTED_BY_EMAIL',
      'SG_SUGGESTED_BY_PHONE',
      'SG_SUGGESTED_BY_ADDRESS',
      'SG_SUGGESTED_BY_CLIENT_CODE',
    ],
    hideFromVerbList: true,
    formFields: [],
    confirmation: {
      title: 'Ignore this suggestion?',
      body:  (issue) => {
        const n = (issue.members || issue.affectedCodes || []).length;
        const basis = (issue.details && issue.details.matchType) || 'shared attribute';
        return `Confirm that the ${n} clients sharing this ${basis} are correctly ` +
               'in separate Signer Groups. The suggestion will be closed without ' +
               'changing any Signer Groups. It will only resurface if the underlying ' +
               'data still matches it on a later data refresh.';
      },
      confirmLabel: 'Yes, ignore suggestion',
      cancelLabel:  'Back',
    },
  },

  // ── Accept proposed Signer Group ───────────────────────────────────
  // Closes SG_SUGGESTED_BY_EMAIL/_ADDRESS/_CLIENT_CODE. Partner MUST pick
  // which cluster member becomes the SG parent — no auto-pick, since the
  // right anchor is rarely obvious (lowest code, existing parent, the
  // entity that owns the matching email, etc.). Options come straight
  // from `issue.members` so the picker shows code + name and excludes
  // anything not in the active PE roster.
  accept_sg_suggestion: {
    label: 'Accept proposed group',
    kind:  'confirm',
    applicableTo: [
      'SG_SUGGESTED_BY_EMAIL',
      'SG_SUGGESTED_BY_PHONE',
      'SG_SUGGESTED_BY_ADDRESS',
      'SG_SUGGESTED_BY_CLIENT_CODE',
    ],
    formFields: (issue) => {
      const members = issue.members || [];
      const clusterKinds = members.map(m => kindOf(m.entityType));
      const fields = [{
        type:    'select',
        name:    'parentCode',
        label:   'Pick the Signer Group parent (every member below will be re-parented to this client)',
        placeholder: '— choose a parent —',
        required: true,
        emptyMessage: 'No eligible parent: a business cannot be the parent of a group that includes an Individual, and a client that is already a child of another Signer Group cannot be one either. Use "Change or Create New Signer Group" on a member to move it first.',
        // Async so we can flag which candidates ALREADY anchor a Signer Group (and which one) — picking
        // one of those folds the others onto it, so the partner should see they may be combining existing
        // groups. When the members are all children of groups outside the proposal (nobody in it can be a
        // parent), the existing anchors of those groups are offered instead (QA 2026-11-03). Falls back to
        // plain code+name labels if the lookup fails.
        loadOptions: async (iss) => {
          const mem   = iss.members || [];
          const codes = mem.map(m => m.code).filter(Boolean);
          const inCluster = new Set(codes);
          const info  = new Map();   // code → { isParent, sgParentCode, memberCount, standalone }
          const external = [];       // groups outside the proposal that hold a cluster member
          if (codes.length) {
            try {
              const r = await fetch(
                `/api/dgSgStructure?codes=${encodeURIComponent(codes.join(','))}`,
                { credentials: 'include' }
              );
              if (r.ok) {
                const data = await r.json();
                for (const g of (data.groups || [])) {
                  for (const gm of (g.members || [])) {
                    info.set(gm.code, {
                      isParent:    gm.isParent,
                      sgParentCode: g.sgParentCode,
                      memberCount: (g.members || []).length,
                      standalone:  g.standalone,
                    });
                  }
                  if (!g.standalone && !inCluster.has(g.sgParentCode)) external.push(g);
                }
              }
            } catch { /* fall back to plain labels */ }
          }
          // A business is never the parent of a Signer Group that holds an Individual (QA, 2026-11-03).
          const businessBlocked = (cohortKinds, parentKind) =>
            cohortKinds.includes('IND') && (parentKind === 'BIZ' || parentKind === 'OBZ');
          const existingKindsFor = new Map();   // external anchor → entity kinds already in its group (not in the proposal)
          const out = mem
            // A client that's currently a CHILD of an existing signer group
            // can't be made the parent of a new one — only existing group
            // parents and standalone clients are eligible anchors. (dgSgStructure
            // marks a standalone / self-anchored client isParent=true; a child
            // is isParent=false. No info ⇒ treat as standalone/eligible.)
            .filter((m) => {
              const i = info.get(m.code);
              return !i || i.isParent;
            })
            .filter(m => !businessBlocked(clusterKinds, kindOf(m.entityType)))
            .map((m) => {
              const base = m.name ? `${m.code} — ${m.name}` : m.code;
              const i = info.get(m.code);
              const headsAGroup = i && i.isParent && !i.standalone && i.memberCount > 1;
              return {
                value: m.code,
                label: headsAGroup
                  ? `${base}  ·  parent of Signer Group ${i.sgParentCode} (${i.memberCount} clients)`
                  : base,
              };
            });
          for (const g of external) {
            const existing = (g.members || []).filter(gm => !inCluster.has(gm.code));
            const existingKinds = existing.map(gm => kindOf(gm.entityType));
            const anchor = (g.members || []).find(gm => gm.code === g.sgParentCode);
            const anchorKind = kindOf(anchor && anchor.entityType);
            if (businessBlocked(clusterKinds, anchorKind)) continue;   // judged on who is joining
            existingKindsFor.set(g.sgParentCode, existingKinds);
            const joining = (g.members || []).filter(gm => inCluster.has(gm.code)).length;
            out.push({
              value: g.sgParentCode,
              label: `${g.sgParentCode}${g.sgParentName ? ' — ' + g.sgParentName : ''}  ·  existing parent of Signer Group ` +
                     `${g.sgParentCode} (${(g.members || []).length} clients${joining ? `, ${joining} of them in this proposal` : ''})`,
            });
          }
          acceptParentContext.set(iss.id, existingKindsFor);
          return out;
        },
      }];
      // Mixed-confirmation gate: the matching Phase 1 confirmation (SMLLC / dependent) rides in the same
      // submission, and the server rejects accept_sg_suggestion without it (or with one it doesn't need).
      // It depends on the chosen parent: a member of the proposal -> the whole cluster is judged; an existing
      // anchor outside it -> a MOVE into that group (who is joining, D1), like dgMovePreview.
      const requiredFor = (values) => {
        const existingKinds = acceptParentContext.get(issue.id)?.get(values.parentCode);
        return existingKinds
          ? requiredConfirmationsForJoin(existingKinds, clusterKinds)
          : requiredMixedConfirmations(clusterKinds);
      };
      for (const token of ['smllc', 'dependent']) {
        fields.push({
          type:         'checkbox',
          name:         token === 'dependent' ? 'mixedConfirmationDependent' : 'mixedConfirmation',
          label:        MIXED_CONFIRM_LABEL[token],
          checkedValue: token,
          required:     true,
          showWhen:     (values) => requiredFor(values).includes(token),
        });
      }
      return fields;
    },
    confirmation: {
      title: 'Accept proposed Signer Group?',
      body:  (issue) => {
        const n = (issue.members || issue.affectedCodes || []).length;
        const basis = (issue.details && issue.details.matchType) || 'shared attribute';
        return `${n} clients share a ${basis}. Pick the parent below — every member ` +
               'will have their Signer Group set to that parent. Any candidate that ' +
               'already heads a Signer Group is flagged in the list, since picking it ' +
               'combines the groups. Other related suggestions are re-checked against ' +
               'the updated groups afterward.';
      },
      confirmLabel: 'Accept and unify Signer Group',
      cancelLabel:  'Back',
    },
  },

  // ── Confirm RG ─────────────────────────────────────────────────────
  // Closes RG_NEEDS_CONFIRMATION / RG_SUGGESTED_BY_SG / RG_SUGGESTED_BY_BG.
  // For _BG, the partner picks the SG anchor from the cluster's SG parents
  // (options come from /api/dgRgAnchorOptions); for the other two the anchor
  // is implicit (primary_client_code for _BY_SG; details.suggested_rg for
  // _NEEDS_CONFIRMATION) so no form fields render.
  confirm_rg: {
    label: 'Confirm relationship group',
    kind:  'confirm',
    applicableTo: ['RG_NEEDS_CONFIRMATION', 'RG_SUGGESTED_BY_SG', 'RG_SUGGESTED_BY_BG'],
    formFields: (issue) => {
      if (issue.issueCode === 'RG_SUGGESTED_BY_BG') {
        return [{
          type:    'select',
          name:    'anchorSg',
          label:   'Pick the Signer Group that should anchor this Relationship Group',
          placeholder: '— choose anchor —',
          required: true,
          loadOptions: async (iss) => {
            const r = await fetch(
              `/api/dgRgAnchorOptions/${encodeURIComponent(iss.id)}`,
              { credentials: 'include' }
            );
            if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
            const data = await r.json();
            return (data.options || []).map((o) => ({
              value: o.sgParentCode,
              label: `${o.sgParentCode}${o.sgParentName ? ' — ' + o.sgParentName : ''}` +
                     ` (${o.memberCount} member${o.memberCount === 1 ? '' : 's'} of cluster)`,
            }));
          },
        }];
      }
      return [];
    },
    confirmation: {
      title: 'Confirm this Relationship Group?',
      body:  (issue) => {
        if (issue.issueCode === 'RG_SUGGESTED_BY_BG') {
          return 'Pick which Signer Group should anchor this Relationship Group. ' +
                 'Every client in the group will be tied to that anchor.';
        }
        // RG_NEEDS_CONFIRMATION (and RG_SUGGESTED_BY_SG) — plain-English
        // attestation. Describe what's in the group using its live
        // composition counts; avoid internal terms (Snowflake / seeding).
        const sg = issue.rgSgCount;
        const cl = issue.rgClientCount;
        const composition = (sg != null && cl != null)
          ? `the ${sg} signer group${sg === 1 ? '' : 's'} and ${cl} client${cl === 1 ? '' : 's'}`
          : 'the clients';
        return `You're confirming that ${composition} in this Relationship Group ` +
               'are correctly grouped together. If something doesn’t belong, close this ' +
               'and move the signer group to a different Relationship Group instead.';
      },
      confirmLabel: 'Yes, these are correctly grouped',
      cancelLabel:  'Back',
    },
  },

  // ── Ignore RG suggestion ───────────────────────────────────────────
  // Closes RG_SUGGESTED_BY_SG / RG_SUGGESTED_BY_BG with no data change.
  // The partner asserts the clients are correctly in their current
  // Relationship Groups (a signer group with none is its own RG by
  // default). Surfaced as a per-row "Ignore suggestion" button via
  // initialVerb, so it stays out of the main verb list.
  ignore_rg_suggestion: {
    label: 'Ignore suggestion',
    kind:  'exclude',
    applicableTo: ['RG_SUGGESTED_BY_SG', 'RG_SUGGESTED_BY_BG'],
    hideFromVerbList: true,
    formFields: [],
    confirmation: {
      title: 'Ignore this suggestion?',
      body:  (issue) => {
        const n = (issue.members || issue.affectedCodes || []).length;
        const basis = issue.issueCode === 'RG_SUGGESTED_BY_BG' ? 'billing group' : 'signer group';
        return `Confirm that the ${n} client(s) sharing this ${basis} are correctly in ` +
               'their current Relationship Groups (a signer group with none is its own ' +
               'Relationship Group by default). The suggestion is closed with no changes ' +
               'and will not be re-suggested this period.';
      },
      confirmLabel: 'Yes, ignore suggestion',
      cancelLabel:  'Back',
    },
  },

  // ── Assign RG (for ORPHAN_CLIENT_RG) ───────────────────────────────
  // Two paths: assign to an existing RG (picker fed by /api/dgRgCandidates)
  // or self-anchor (this client becomes its own RG, using its SG parent
  // — or itself when standalone — as the anchor).
  assign_rg: {
    label: 'Assign relationship group',
    kind:  'move',
    applicableTo: ['ORPHAN_CLIENT_RG'],
    formFields: [
      {
        type:    'radio',
        name:    'kind',
        label:   'How should this client be assigned?',
        required: true,
        options: [
          { value: 'self',     label: 'Self-anchor — this client becomes its own Relationship Group' },
          { value: 'existing', label: 'Assign to an existing Relationship Group' },
        ],
      },
      {
        // Searchable picker over ALL existing RG anchors (parity with
        // the SG picker), not just the suggestion-derived candidates —
        // an orphan client often has zero/one candidate, which left the
        // partner with nothing useful to pick from. includeOrphans also
        // surfaces ungrouped clients (incl. other partners') so this
        // orphan can be grouped under one, starting a new RG.
        type:      'anchorPicker',
        anchorType: 'rg',
        includeOrphans: true,
        name:      'targetRg',
        label:     'Pick the existing Relationship Group',
        placeholder: 'Start typing a Relationship Group code or name…',
        required:  true,
        showWhen:  (values) => values.kind === 'existing',
        loadPreferred: loadMyRgAnchors,
        // You can't assign a client to its OWN code as a Relationship Group —
        // drop it from both the quick-picks and the search (parity with
        // rescue_to_rg). The backend also rejects the client's current RG
        // anchor as a no-op.
        excludeAnchor: (issue) => issue && issue.primaryClientCode,
      },
    ],
    confirmation: {
      title: 'Assign relationship group',
      body:  (issue) =>
        `Pick how to give ${issue.primaryClientCode} a Relationship Group. ` +
        'Self-anchor when no other clients are related; choose an existing ' +
        'Relationship Group to merge this client into an established relationship.',
      confirmLabel: 'Apply',
      cancelLabel:  'Back',
    },
  },

  // ── Confirm standalone (Phase 4) ───────────────────────────────────
  // Partner affirms a flagged Individual is truly solo (no relationship
  // group). Closes the issue with no data change.
  confirm_standalone: {
    label: 'Confirm standalone',
    kind:  'confirm',
    applicableTo: ['STANDALONE_INDIVIDUAL_NEEDS_REVIEW'],
    formFields: [],
    confirmation: {
      title: 'Confirm standalone individual?',
      body:  (issue) =>
        `${issue.primaryClientCode} is currently in a one-member Signer Group ` +
        'and a one-Signer-Group Relationship Group. Confirming attests this client really ' +
        'has no related entities — the issue closes with no data change.',
      confirmLabel: 'Yes, confirm standalone',
      cancelLabel:  'Back',
    },
  },

  // ── Rescue to RG (Phase 4) ─────────────────────────────────────────
  // Partner places the flagged Individual into an existing RG. Picker
  // covers ALL existing RG anchors (searchable), not just the
  // suggestion-derived candidates.
  rescue_to_rg: {
    label: 'Rescue into existing Relationship Group',
    kind:  'move',
    applicableTo: ['STANDALONE_INDIVIDUAL_NEEDS_REVIEW'],
    formFields: [
      {
        type:       'anchorPicker',
        anchorType: 'rg',
        includeOrphans: true,   // also offer ungrouped orphans → start a new RG
        name:       'targetRg',
        label:      'Pick the Relationship Group',
        placeholder: 'Start typing a Relationship Group code or name…',
        required:   true,
        // Most rescues go into one of the partner's OWN Relationship Groups —
        // surface those as one-click quick-picks; the full search stays below.
        loadPreferred: loadMyRgAnchors,
        // You can't rescue a standalone into its OWN Relationship Group — drop
        // the client's own code (its solo RG anchor) from the options.
        excludeAnchor: (issue) => issue && issue.primaryClientCode,
      },
    ],
    confirmation: {
      title: 'Rescue into existing Relationship Group',
      body:  (issue) =>
        `Move ${issue.primaryClientCode} into an existing Relationship Group. ` +
        'The client\'s Signer Group composition stays unchanged; only their ' +
        'Relationship Group anchor updates.',
      confirmLabel: 'Apply',
      cancelLabel:  'Back',
    },
  },

  // ── Choose a new parent after the old one was marked NLC ──────────
  // Resolves NLC_PARENT_NEEDS_SUCCESSOR. Re-homes the WHOLE group (every partner's clients under
  // the NLC client), as Mark NLC's restructure options do. Review stamps are untouched.
  rehome_nlc_group: {
    label: 'Choose a new parent',
    kind:  'move',
    applicableTo: ['NLC_PARENT_NEEDS_SUCCESSOR'],
    blockedReason: rehomeBlockedReason,
    formFields: (issue) => {
      const d    = issue.details || {};
      const isSg = d.layer !== 'rg';
      const cands = Array.isArray(d.candidates) ? d.candidates : [];
      return [
        {
          type:     'radio',
          name:     'resolution',
          label:    isSg ? 'Who leads the Signer Group now?' : 'Who anchors the Relationship Group now?',
          required: true,
          options:  isSg
            ? [
                { value: 'promote', label: 'Promote one of the remaining clients to be the new Signer Group parent' },
                { value: 'disband', label: 'Disband the Signer Group — each remaining client becomes its own Signer Group' },
                { value: 'move',    label: 'Move all of them into another existing Signer Group' },
              ]
            : [
                { value: 'promote', label: 'Anchor the Relationship Group on one of its remaining Signer Groups' },
                { value: 'disband', label: 'Disband the Relationship Group — each remaining Signer Group becomes its own Relationship Group' },
                { value: 'merge',   label: 'Merge into another existing Relationship Group' },
              ],
        },
        {
          type:        'select',
          name:        'newParent',
          label:       isSg ? 'New Signer Group parent' : 'New Relationship Group anchor (a Signer Group)',
          placeholder: isSg ? '— pick a client —' : '— pick a Signer Group —',
          required:    true,
          showWhen:    (values) => values.resolution === 'promote',
          options:     cands.map(c => ({
            value: c.clientCode,
            label: rehomeLabel(c) + (!isSg && c.memberCount
              ? ` (${c.memberCount} client${c.memberCount === 1 ? '' : 's'})` : ''),
          })),
        },
        isSg ? REHOME_SG_MOVE_FIELD : REHOME_RG_MERGE_FIELD,
      ];
    },
    confirmation: {
      title: 'Choose who leads the group',
      body:  (issue) => {
        const d     = issue.details || {};
        const who   = d.nlcClientName ? `${d.nlcClientCode} (${d.nlcClientName})` : d.nlcClientCode;
        const n     = d.memberCount || (issue.affectedCodes || []).length;
        const clients = `${n} client${n === 1 ? '' : 's'}`;
        const mine  = d.partnerMemberCount && d.partnerMemberCount !== n
          ? ` ${d.partnerMemberCount} of them ${d.partnerMemberCount === 1 ? 'is' : 'are'} yours; the choice applies to the whole group.`
          : '';
        return (d.layer === 'rg'
          ? `${who} was marked No Longer Client and was the anchor of a Relationship Group with ${clients} — choose which Signer Group anchors it now.`
          : `${who} was marked No Longer Client and was the signer group parent of ${clients} — choose who leads the group.`)
          + mine
          + ' Nobody’s review sign-off is undone by this.';
      },
      confirmLabel: 'Apply',
      cancelLabel:  'Back',
    },
  },

  // dismiss removed from the partner UI — every Phase 1/2/3 issue must
  // result in an explicit Confirm / Pick / Assign / Move decision. The
  // backend handler still exists for legacy audit rows, but no UI surface
  // exposes it.

  // ── Split the group (redirect to row drilldown) ────────────────────
  // Offered alongside Confirm SMLLC / Confirm dependent / Pick truth so
  // partners always have an "actually peel the offending member out"
  // alternative. Clicking this verb doesn't hit /api/diResolveAction
  // and doesn't show a confirmation step — it just expands the issue
  // row (where the per-member Move… / Make SG Parent / Mark NLC
  // buttons live) and closes the modal. `openRowDrilldown: true` is
  // the marker ResolveModal looks for.
  split_from_group: {
    label: 'Split the group instead',
    kind:  'move',
    // Deliberately NOT applicable to SG_SOURCE_DISAGREEMENT — at that
    // stage the partner should only pick which source (Knuula vs PE)
    // reflects reality. Any structural splitting happens later, after
    // the SG is confirmed and downstream rules re-evaluate.
    applicableTo: [
      'SG_BUSINESS_INDIVIDUAL_MIXED',
      'SG_MULTI_INDIVIDUAL_PARENT',
      'SG_TRUST_ESTATE_MIXED',
      'SG_NON_SMLLC_MIXED',
      // KNUULA_MISSING_SG: the "Assign Signer Group" button points the partner
      // at the per-member "Change or Create New Signer Group" action in the
      // expanded row (make its own SG, or add to an existing one).
      'KNUULA_MISSING_SG',
    ],
    formFields: [],
    noApiSubmit:      true,
    openRowDrilldown: true,
    confirmation: {
      // Unused when openRowDrilldown is true (the modal short-circuits
      // before reaching ConfirmationStep) but kept as a defensive
      // fallback if the flag ever gets dropped.
      title: 'Split the Signer Group',
      body:  () =>
        'Expand this issue row to see each member card; the offending member ' +
        'has a Move… button that peels them out to their own Signer Group or ' +
        'an existing one.',
      confirmLabel: 'Got it',
      cancelLabel:  'Back',
    },
  },

  // FUTURE VERBS:
  //   pick_new_parent    SG_PARENT_INACTIVE_IN_PE (cascade re-parenting)
};

// Returns the verbs applicable to a given issue_code, sorted by kind order
// then label. Used by the modal to render the button list.
export function verbsForIssue(issueCode) {
  const kindOrder = Object.fromEntries(VERB_KINDS.map((k, i) => [k, i]));
  return Object.entries(VERB_CONFIG)
    .filter(([, cfg]) =>
      cfg.applicableTo === '*' ||
      (Array.isArray(cfg.applicableTo) && cfg.applicableTo.includes(issueCode))
    )
    .map(([key, cfg]) => ({ key, ...cfg }))
    .sort((a, b) =>
      (kindOrder[a.kind] - kindOrder[b.kind]) || a.label.localeCompare(b.label)
    );
}
